<script setup lang="ts">
import { generateRandomString } from '@/vf'
import { computed } from 'vue'
import draggable from 'vuedraggable'
import { formElements } from './formElements'
import FormBuilderTopicSelector from './FormBuilderTopicSelector.vue'
import type { EditorNode } from './types'
import type { FormBuilder } from './useFormBuilder'

const { formBuilder } = defineProps<{ formBuilder: FormBuilder }>()
const node = computed(() => formBuilder.selectedNode.value)
const palette = computed(() => node.value ? formElements.get(node.value.type) : null)

const hasOptions = computed(() => !!palette.value?.hasOptions)
const hasPlaceholder = computed(() => !!palette.value?.hasPlaceholder)
const hasFileConfig = computed(() => !!palette.value?.hasFileConfig)
const isLayout = computed(() => !!palette.value?.isLayout)
const isRegularField = computed(() => !!palette.value && !palette.value.isLayout && !palette.value.isSubmit)
const isTopic = computed(() => node.value?.type === 'topic')

const isRequired = computed({
    get: () => !!node.value?.required,
    set: (val: boolean) => {
        if (!node.value) {
            return
        }
        node.value.required = val
    },
})

function clearIssueSubtitleFlag(nodes: EditorNode[], exceptId: string) {
    for (const n of nodes) {
        if (n._id !== exceptId) { n.useAsIssueSubtitle = false }
        clearIssueSubtitleFlag(n.children, exceptId)
    }
}

const useAsIssueSubtitle = computed({
    get: () => !!node.value?.useAsIssueSubtitle,
    set: (val: boolean) => {
        if (!node.value) {
            return
        }
        if (val) {
            clearIssueSubtitleFlag(formBuilder.nodes.value, node.value._id)
        }
        node.value.useAsIssueSubtitle = val
    },
})

function addOption() {
    if (!node.value) return
    const n = node.value.options.length + 1
    node.value.options.push({ label: `Option ${n}`, value: `option_${generateRandomString(4)}` })
}

function removeOption(idx: number) {
    node.value?.options.splice(idx, 1)
}
</script>

<template>
    <div class="fb-props">
        <template v-if="!node">
            <div class="fb-props__empty">
                <i class="fa fa-mouse-pointer fa-lg"></i>
                <div class="mt-2">{{ $t("components:form_builder.no_field_selected") }}</div>
            </div>
        </template>

        <template v-else>
            <div class="fb-props__title">{{ $t("components:form_builder.properties") }}</div>

            <div v-if="isRegularField" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.field_name") }}</label>
                <input v-model="node.name" class="form-control form-control-sm" />
                <div class="fb-hint">{{ $t("components:form_builder.field_name_hint") }}</div>
            </div>

            <div v-if="!isLayout" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.label") }}</label>
                <input v-model="node.label" class="form-control form-control-sm" />
            </div>

            <div v-if="isRegularField" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.help_text") }}</label>
                <input v-model="node.help" class="form-control form-control-sm" />
            </div>

            <div v-if="hasPlaceholder" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.placeholder") }}</label>
                <input v-model="node.placeholder" class="form-control form-control-sm" />
            </div>

            <div v-if="isRegularField" class="fb-field">
                <label class="fb-label fb-checkbox">
                    <input type="checkbox" v-model="isRequired" />
                    {{ $t("components:form_builder.required") }}
                </label>
            </div>

            <div v-if="isRegularField && !hasFileConfig" class="fb-field">
                <label class="fb-label fb-checkbox">
                    <input type="checkbox" v-model="useAsIssueSubtitle" />
                    {{ $t("components:form_builder.use_as_issue_subtitle") }}
                </label>
                <div class="fb-hint">{{ $t("components:form_builder.use_as_issue_subtitle_hint") }}</div>
            </div>

            <div v-if="isRegularField" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.validation") }}</label>
                <input v-model="node.validation" class="form-control form-control-sm" :placeholder="$t('components:form_builder.validation_placeholder')" />
                <div class="fb-hint" v-html="$t('components:form_builder.validation_hint')" />
            </div>

            <div v-if="hasFileConfig" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.upload_accept") }}</label>
                <input
                    v-model="node.accept"
                    class="form-control form-control-sm"
                    :placeholder="$t('components:form_builder.upload_accept_placeholder')"
                />
                <div class="fb-hint">{{ $t("components:form_builder.upload_accept_hint") }}</div>
            </div>

            <div v-if="hasFileConfig" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.upload_max_size") }}</label>
                <input v-model.number="node.maxFileSize" type="number" min="1" class="form-control form-control-sm" />
                <div class="fb-hint">{{ $t("components:form_builder.upload_max_size_hint") }}</div>
            </div>

            <div v-if="hasFileConfig" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.upload_max_files") }}</label>
                <input v-model.number="node.maxFiles" type="number" min="1" class="form-control form-control-sm" />
                <div class="fb-hint">{{ $t("components:form_builder.upload_max_files_hint") }}</div>
            </div>

            <div v-if="isLayout" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.column_count") }}</label>
                <select v-model.number="node.columns" class="form-control form-control-sm">
                    <option :value="1">1</option>
                    <option :value="2">2</option>
                    <option :value="3">3</option>
                    <option :value="4">4</option>
                </select>
            </div>

            <div v-if="hasOptions" class="fb-field">
                <label class="fb-label">{{ $t("components:form_builder.options") }}</label>
                <draggable v-model="node.options" item-key="value" handle=".opt-handle" class="fb-options">
                    <template #item="{ element: opt, index }">
                        <div class="fb-option">
                            <i class="fa fa-bars opt-handle fb-option__handle"></i>
                            <input v-model="opt.label" class="form-control form-control-sm" :placeholder="$t('components:form_builder.option_label')" />
                            <input v-model="opt.value" class="form-control form-control-sm" :placeholder="$t('components:form_builder.option_value')" />
                            <button type="button" class="fb-option__remove" :title="$t('components:form_builder.remove_option')" @click="removeOption(index)">
                                <i class="fa fa-times"></i>
                            </button>
                        </div>
                    </template>
                </draggable>
                <button type="button" class="btn btn-sm btn-outline-secondary mt-2 w-100" @click="addOption">
                    <i class="fa fa-plus"></i> {{ $t("components:form_builder.add_option") }}
                </button>
            </div>

            <FormBuilderTopicSelector v-if="isTopic" :node="node" />
        </template>
    </div>
</template>

<style lang="scss" scoped>
.fb-props {
    overflow-y: auto;
    padding-left: 1rem;
    border-left: 1px solid #dee2e6;

    &__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 200px;
        color: #ced4da;
        font-size: 0.875rem;
        text-align: center;
    }

    &__title {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #6c757d;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid #dee2e6;
        margin-bottom: 0.75rem;
    }
}

.fb-option__remove {
    flex-shrink: 0;
    padding: 0.2rem 0.4rem;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: #adb5bd;
    cursor: pointer;
    line-height: 1;
    transition: background 0.1s, color 0.1s;

    &:hover {
        background: #f8d7da;
        color: #dc3545;
        border-color: #f5c2c7;
    }
}
</style>
